
import { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import BlogNavigation from './components/BlogNavigation';
import BlogCard from './components/BlogCard';
import Footer from '../home/components/Footer';
import { blogPosts } from '../../mocks/blogPosts';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://jobos.dev';

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const post = blogPosts.find((p) => p.slug === slug);
  const relatedPosts = blogPosts
    .filter((p) => p.slug !== slug && p.category === post?.category)
    .slice(0, 3);
  const otherPosts = blogPosts.filter((p) => p.slug !== slug).slice(0, 3);
  const suggestedPosts = relatedPosts.length > 0 ? relatedPosts : otherPosts;

  // -------------------------------------------------------------------------
  // SEO & Schema handling
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!post) {
      navigate('/blog', { replace: true });
      return;
    }

    // ---- Title ------------------------------------------------------------
    document.title = `${post.title} | JobOS Blog`;

    // ---- Helper to create / update meta tags -------------------------------
    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as
        | HTMLMetaElement
        | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    // ---- Standard meta -----------------------------------------------------
    setMeta('description', post.excerpt);
    setMeta('robots', 'index, follow');

    // ---- Open Graph ---------------------------------------------------------
    setMeta('og:title', `${post.title} | JobOS Blog`, true);
    setMeta('og:description', post.excerpt, true);
    setMeta('og:url', `${SITE_URL}/blog/${post.slug}`, true);
    setMeta('og:type', 'article', true);
    setMeta('og:image', post.coverImage, true);
    setMeta('og:image:width', '1200', true);
    setMeta('og:image:height', '630', true);
    setMeta('article:published_time', post.date, true);
    setMeta('article:author', post.author.name, true);
    setMeta('article:section', post.category, true);

    // ---- Twitter ------------------------------------------------------------
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', `${post.title} | JobOS Blog`);
    setMeta('twitter:description', post.excerpt);
    setMeta('twitter:image', post.coverImage);

    // ---- Canonical link -----------------------------------------------------
    let canonical = document.querySelector(
      'link[rel="canonical"]'
    ) as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${SITE_URL}/blog/${post.slug}`);

    // ---- Remove old JSON‑LD scripts -----------------------------------------
    document
      .querySelectorAll('script[data-schema-page="blog-post"]')
      .forEach((el) => el.remove());

    // ---- Add JSON‑LD --------------------------------------------------------
    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: post.title,
        description: post.excerpt,
        image: post.coverImage,
        datePublished: post.date,
        dateModified: post.date,
        author: {
          '@type': 'Person',
          name: post.author.name,
          jobTitle: post.author.role,
        },
        publisher: {
          '@type': 'Organization',
          name: 'JobOS',
          url: SITE_URL,
          logo: {
            '@type': 'ImageObject',
            url: `${SITE_URL}/og-image.png`,
          },
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `${SITE_URL}/blog/${post.slug}`,
        },
        articleSection: post.category,
        keywords: post.tags.join(', '),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Blog',
            item: `${SITE_URL}/blog`,
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: post.title,
            item: `${SITE_URL}/blog/${post.slug}`,
          },
        ],
      },
    ];

    schemas.forEach((schema) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-schema-page', 'blog-post');
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    // ---- Scroll to top ------------------------------------------------------
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // Cleanup JSON‑LD when component unmounts or slug changes
    return () => {
      document
        .querySelectorAll('script[data-schema-page="blog-post"]')
        .forEach((el) => el.remove());
    };
  }, [slug, post, navigate]);

  // -------------------------------------------------------------------------
  // If the post cannot be found we already redirected, but render nothing
  // to avoid React warnings.
  // -------------------------------------------------------------------------
  if (!post) {
    return null;
  }

  // -------------------------------------------------------------------------
  // Split the content into paragraphs for simple rendering.
  // -------------------------------------------------------------------------
  const contentParagraphs = post.content.split('\n\n');

  // -------------------------------------------------------------------------
  // Render the blog post page.
  // -------------------------------------------------------------------------
  return (
    <div className="blog-post-page">
      {/* Navigation bar (could contain breadcrumbs, category links, etc.) */}
      <BlogNavigation />

      <main className="container mx-auto px-4 py-8">
        <article className="prose prose-lg mx-auto">
          <h1>{post.title}</h1>
          <p className="text-sm text-gray-500">
            {post.author.name} • {new Date(post.date).toLocaleDateString()} •{' '}
            {post.category}
          </p>

          {/* Cover image */}
          {post.coverImage && (
            <img
              src={post.coverImage}
              alt={post.title}
              className="w-full h-auto my-6 rounded"
            />
          )}

          {/* Content paragraphs */}
          {contentParagraphs.map((para, idx) => (
            <p key={idx}>{para}</p>
          ))}

          {/* Tags */}
          <div className="mt-6">
            {post.tags.map((tag) => (
              <Link
                key={tag}
                to={`/blog/tag/${tag}`}
                className="inline-block bg-gray-200 text-gray-700 px-2 py-1 mr-2 rounded"
              >
                #{tag}
              </Link>
            ))}
          </div>
        </article>

        {/* Suggested / related posts */}
        <section className="mt-12">
          <h2 className="text-2xl font-semibold mb-4">
            {relatedPosts.length > 0 ? 'Related Posts' : 'You Might Also Like'}
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {suggestedPosts.map((p) => (
              <BlogCard key={p.slug} post={p} />
            ))}
          </div>
        </section>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
}
